import React, {useState, useRef, useEffect} from 'react';
import JSZip from 'jszip';
import XLSX from 'xlsx-js-style';
import {printService} from '../../services';
import {BillOfLadingData} from '../../prints/BillOfLading';
import {Car} from '../../types';
import {LoadingOverlay} from '../ui/LoadingOverlay';
import {buildBillsForPrint} from '../../utils/carUtils';
import {batchProcess} from '../../utils/asyncUtils';
import {Modal} from '../modals/Modal';
import {PrintBookingConfirmationOptions} from './ContextMenu';

export type BulkActionKey =
    | 'change_voyage'
    | 'change_consignee'
    | 'change_destination'
    | 'mark_loaded'
    | 'mark_unloaded'
    | 'set_pricing'
    | 'set_length_pricing'
    | 'add_insurance'
    | 'remove_insurance'
    | 'combine_bl'
    | 'separate_bl'
    | 'print_bill_of_lading'
    | 'print_booking_confirmation'
    | 'export_excel'
    | 'delete';

export interface ActionGroup {
    title: string;
    actions: { key: BulkActionKey; label: string }[];
}

interface BulkActionsMenuProps {
    selectedCarIds: string[];
    cars: Car[];
    actionGroups: ActionGroup[];
    onAction: (action: BulkActionKey) => void;
    convertCarToBillOfLadingData?: (car: Car) => BillOfLadingData | null;
    onPrintBookingConfirmation?: (selectedCars: Car[], options: PrintBookingConfirmationOptions) => Promise<void>;
    showToast?: (message: string, type: 'success' | 'error' | 'warning' | 'info') => void;
    displayCount?: number; // Override displayed count (for select-all-across-pages)
    resolveSelectedCars?: () => Promise<Car[]>; // Fetch full car objects when selectAllAcrossPages is active
}

export const BulkActionsMenu: React.FC<BulkActionsMenuProps> = ({
                                                                    selectedCarIds,
                                                                    cars,
                                                                    actionGroups,
                                                                    onAction,
                                                                    onPrintBookingConfirmation,
                                                                    showToast,
                                                                    displayCount,
                                                                    resolveSelectedCars,
                                                                }) => {
    // Single source of truth for export column configuration
    const EXPORT_COLUMNS = [
        {key: 'bookingRef', label: 'Booking Ref', header: 'BOOKING REF'},
        {key: 'makeModel', label: 'Make/Model', header: 'MAKE/MODEL'},
        {key: 'chassisNumber', label: 'Chassis NR.', header: 'CHASSIS NR.'},
        {key: 'shipper', label: 'Shipper', header: 'SHIPPER'},
        {key: 'consignee', label: 'Consignee', header: 'CONSIGNEE'},
        {key: 'address', label: 'Address', header: 'ADDRESS'},
        {key: 'destination', label: 'Destination', header: 'DESTINATION'},
        {key: 'year', label: 'Year', header: 'YEAR'},
        {key: 'shortChassis', label: 'Short Chassis', header: 'SHORT CHASSIS'},
    ];

    const DIMENSION_COLUMNS = [
        {key: 'length', label: 'Length', header: 'LENGTH'},
        {key: 'width', label: 'Width', header: 'WIDTH'},
        {key: 'height', label: 'Height', header: 'HEIGHT'},
        {key: 'cbm', label: 'CBM', header: 'CBM'},
        {key: 'weight', label: 'Weight', header: 'WEIGHT'},
    ];

    // Address defaults off; every other column defaults on.
    const getDefaultColumnSelection = () => Object.fromEntries(EXPORT_COLUMNS.map(col => [col.key, col.key !== 'address']));

    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const [showPrintBLSubmenu, setShowPrintBLSubmenu] = useState(false);
    const [showPrintBookingConfirmationSubmenu, setShowPrintBookingConfirmationSubmenu] = useState(false);
    const [isOriginalBL, setIsOriginalBL] = useState(false);
    const [includeRelated, setIncludeRelated] = useState(true);
    const [includeRelatedBookingConfirmation, setIncludeRelatedBookingConfirmation] = useState(true);
    const [generatingBL, setGeneratingBL] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>(getDefaultColumnSelection);
    const [includeDimensions, setIncludeDimensions] = useState(false);
    const printBLSubmenuRef = useRef<HTMLDivElement>(null);
    const printBLButtonRef = useRef<HTMLButtonElement | null>(null);
    const printBookingConfirmationSubmenuRef = useRef<HTMLDivElement>(null);
    const printBookingConfirmationButtonRef = useRef<HTMLButtonElement | null>(null);

    // Get selected cars - uses resolveSelectedCars callback if available (for across-pages), otherwise local filter
    const getSelectedCars = async (): Promise<Car[]> => {
        if (resolveSelectedCars) {
            return resolveSelectedCars();
        }
        return cars.filter(car => selectedCarIds.includes(car.id));
    };

    // Close print BL submenu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (printBLSubmenuRef.current && !printBLSubmenuRef.current.contains(event.target as Node) &&
                printBLButtonRef.current && !printBLButtonRef.current.contains(event.target as Node)) {
                setShowPrintBLSubmenu(false);
            }
        };
        if (showPrintBLSubmenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPrintBLSubmenu]);

    // Close print Booking Confirmation submenu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (printBookingConfirmationSubmenuRef.current && !printBookingConfirmationSubmenuRef.current.contains(event.target as Node) &&
                printBookingConfirmationButtonRef.current && !printBookingConfirmationButtonRef.current.contains(event.target as Node)) {
                setShowPrintBookingConfirmationSubmenu(false);
            }
        };
        if (showPrintBookingConfirmationSubmenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showPrintBookingConfirmationSubmenu]);

    // Close actions menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (printBLSubmenuRef.current?.contains(target) || printBLButtonRef.current?.contains(target)) {
                return;
            }
            if (printBookingConfirmationSubmenuRef.current?.contains(target) || printBookingConfirmationButtonRef.current?.contains(target)) {
                return;
            }
            if (!(event.target as Element).closest('[data-actions-menu]')) {
                setShowActionsMenu(false);
            }
        };
        if (showActionsMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showActionsMenu]);


    const handlePrintSingleBL = async () => {
        if (selectedCarIds.length !== 1) {
            showToast?.('Please select exactly one car', 'warning');
            return;
        }

        const selectedCar = cars.find(car => car.id === selectedCarIds[0]);
        if (!selectedCar) {
            showToast?.('Selected car not found. Select a single car from the table.', 'error');
            return;
        }

        if (!selectedCar.voyage) {
            showToast?.('Car must have a voyage assigned to generate Bill of Lading', 'warning');
            return;
        }

        // Expand to the whole combined group if this unit is on a shared BL.
        const [blData] = buildBillsForPrint([selectedCar], cars, { isOriginal: isOriginalBL, includeRelated });
        if (!blData) return;

        setGeneratingBL(true);
        setShowActionsMenu(false);
        setShowPrintBLSubmenu(false);

        try {
            const type = isOriginalBL ? 'Original' : 'Copy';
            const filename = `Bill of Lading ${blData.blNumber} ${type}.pdf`;
            await printService.generateBillOfLading(blData, filename);
            showToast?.('Bill of Lading generated successfully', 'success');
        } catch (error) {
            console.error('Error generating Bill of Lading:', error);
            showToast?.('Failed to generate Bill of Lading', 'error');
        } finally {
            setGeneratingBL(false);
        }
    };

    const handlePrintCombinedBL = async () => {
        const effectiveLen = displayCount ?? selectedCarIds.length;
        if (effectiveLen < 2) {
            showToast?.('Please select at least two cars for combined BL', 'warning');
            return;
        }

        setGeneratingBL(true);
        setShowActionsMenu(false);
        setShowPrintBLSubmenu(false);

        const selectedCars = await getSelectedCars();
        // Expand any combined groups within the selection into shared BLs (deduped).
        const blDataList = buildBillsForPrint(selectedCars, selectedCars, { isOriginal: isOriginalBL, includeRelated });

        if (blDataList.length === 0) {
            showToast?.('No valid cars found for Bill of Lading', 'error');
            setGeneratingBL(false);
            return;
        }

        try {
            const type = isOriginalBL ? 'Original' : 'Copy';
            const voyageCode = blDataList[0].voyageNumber || 'COMBINED';
            const filename = `Combined Bill of Lading ${voyageCode} ${type}.pdf`;
            await printService.generateCombinedBillOfLading({bills: blDataList}, filename);
            showToast?.('Combined Bill of Lading generated successfully', 'success');
        } catch (error) {
            console.error('Error generating Combined Bill of Lading:', error);
            showToast?.('Failed to generate Combined Bill of Lading', 'error');
        } finally {
            setGeneratingBL(false);
        }
    };

    const handlePrintBLZip = async () => {
        const effectiveLen = displayCount ?? selectedCarIds.length;
        if (effectiveLen < 2) {
            showToast?.('Please select at least two cars for ZIP', 'warning');
            return;
        }

        setGeneratingBL(true);
        setShowActionsMenu(false);
        setShowPrintBLSubmenu(false);

        const selectedCars = await getSelectedCars();
        // Expand any combined groups within the selection into shared BLs (deduped).
        const blDataList = buildBillsForPrint(selectedCars, selectedCars, { isOriginal: isOriginalBL, includeRelated });

        if (blDataList.length === 0) {
            showToast?.('No valid cars found for Bill of Lading', 'error');
            setGeneratingBL(false);
            return;
        }

        try {
            const zip = new JSZip();
            const type = isOriginalBL ? 'Original' : 'Copy';

            // Process PDFs in parallel batches of 3
            const results = await batchProcess(
                blDataList,
                async (blData) => {
                    try {
                        const blob = await printService.getBillOfLadingBlob(blData);
                        return {blData, blob, error: null};
                    } catch (error) {
                        console.error(`Error generating PDF for ${blData.blNumber}:`, error);
                        return {blData, blob: null, error};
                    }
                },
                3
            );

            // Add successful PDFs to zip
            for (const result of results) {
                if (result.blob) {
                    const filename = `Bill of Lading ${result.blData.blNumber} ${type}.pdf`;
                    zip.file(filename, result.blob);
                }
            }

            const zipBlob = await zip.generateAsync({type: 'blob'});
            const voyageCode = blDataList[0].voyageNumber || 'MULTIPLE';
            const zipFilename = `Bills of Lading ${voyageCode} ${type}.zip`;
            const url = URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = zipFilename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast?.('Bills of Lading ZIP generated successfully', 'success');
        } catch (error) {
            console.error('Error generating Bills of Lading ZIP:', error);
            showToast?.('Failed to generate Bills of Lading ZIP', 'error');
        } finally {
            setGeneratingBL(false);
        }
    };

    const handlePrintBookingConfirmation = async () => {
        if (!onPrintBookingConfirmation) {
            showToast?.('Print booking confirmation is not available', 'info');
            return;
        }

        setShowActionsMenu(false);
        setShowPrintBookingConfirmationSubmenu(false);

        const selectedCars = await getSelectedCars();
        if (selectedCars.length === 0) {
            showToast?.('No cars selected', 'warning');
            return;
        }

        try {
            await onPrintBookingConfirmation(selectedCars, {includeRelated: includeRelatedBookingConfirmation});
        } catch (error) {
            console.error('Error generating booking confirmation:', error);
            showToast?.('Failed to generate booking confirmation', 'error');
        }
    };

    const handleExportToExcel = () => {
        if (selectedCarIds.length === 0) {
            showToast?.('No cars selected', 'warning');
            return;
        }

        // Reset all columns to selected when opening modal
        setSelectedColumns(getDefaultColumnSelection());
        setIncludeDimensions(false);

        setShowActionsMenu(false);
        setShowExportModal(true);
    };

    const handleConfirmExport = async () => {
        // Validate at least one column selected
        const hasSelectedColumn = Object.values(selectedColumns).some(v => v);
        if (!hasSelectedColumn) {
            showToast?.('Please select at least one column', 'warning');
            return;
        }

        setShowExportModal(false);

        try {
            const selectedCars = await getSelectedCars();

            // Filter columns based on selection and get indices
            const activeColumns = EXPORT_COLUMNS
                .map((col, index) => ({...col, index}))
                .filter(col => selectedColumns[col.key]);
            const activeDimensionColumns = includeDimensions
                ? DIMENSION_COLUMNS.map((col, i) => ({...col, index: EXPORT_COLUMNS.length + i}))
                : [];
            const headers = [
                ...activeColumns.map(col => col.header),
                ...activeDimensionColumns.map(col => col.header),
            ];
            const columnIndices = [
                ...activeColumns.map(col => col.index),
                ...activeDimensionColumns.map(col => col.index),
            ];

            // Track which rows are related units for styling
            const rowIsRelated: boolean[] = [];

            // Build full data first
            const fullData: string[][] = [];
            selectedCars.forEach(car => {
                const make = car.make?.name || '';
                const model = car.model?.name || '';
                const makeModel = make && model
                    ? `${make} ${model}`
                    : car.manual_make_model || `${make}${model}`;
                const chassisNumber = car.chassis_number || '';
                const shortChassis = chassisNumber.length >= 6
                    ? chassisNumber.slice(-6)
                    : chassisNumber;

                // Add main car row (9 base columns + 5 dimension columns) — order must match EXPORT_COLUMNS
                fullData.push([
                    (car.booking_reference || '').toUpperCase(),
                    makeModel.toUpperCase(),
                    chassisNumber.toUpperCase(),
                    (car.shipper?.shortname || car.shipper_name || car.shipper?.name || '').toUpperCase(),
                    (car.consignee?.name || car.consignee_name || car.name || '').toUpperCase(),
                    (car.address || car.consignee?.address || '').toUpperCase(),
                    (car.port?.port || car.port_code || '').toUpperCase(),
                    String(car.year || ''),
                    shortChassis.toUpperCase(),
                    car.length != null ? String(car.length) : '',
                    car.width != null ? String(car.width) : '',
                    car.height != null ? String(car.height) : '',
                    car.cbm != null ? String(car.cbm) : '',
                    car.weight != null ? String(car.weight) : '',
                ]);
                rowIsRelated.push(false);

                // Add related cars if any (sorted: Attached first, then STC)
                if (car.related_cars && car.related_cars.length > 0) {
                    const sortedRelatedCars = [...car.related_cars].sort((a, b) => {
                        if (a.relationship_type === 'Attached' && b.relationship_type === 'STC') return -1;
                        if (a.relationship_type === 'STC' && b.relationship_type === 'Attached') return 1;
                        return 0;
                    });
                    sortedRelatedCars.forEach(relatedCar => {
                        const relMake = relatedCar.make?.name || '';
                        const relModel = relatedCar.model?.name || '';
                        let relMakeModel = '';
                        if (relMake && relModel) {
                            relMakeModel = `${relMake} ${relModel}`;
                        } else if (relMake || relModel) {
                            relMakeModel = `${relMake}${relModel}`;
                        } else if (relatedCar.manual_make_model) {
                            relMakeModel = relatedCar.manual_make_model;
                        } else {
                            relMakeModel = makeModel;
                        }
                        const relChassisNumber = relatedCar.chassis_number || '';
                        const relShortChassis = relChassisNumber.length >= 6
                            ? relChassisNumber.slice(-6)
                            : relChassisNumber;

                        const prefix = relatedCar.relationship_type === 'STC' ? '└ STC: ' : '└ ATT: ';

                        fullData.push([
                            (car.booking_reference || '').toUpperCase(),
                            (prefix + relMakeModel).toUpperCase(),
                            relChassisNumber.toUpperCase(),
                            (car.shipper?.name || car.shipper_name || '').toUpperCase(),
                            (car.consignee?.name || car.consignee_name || car.name || '').toUpperCase(),
                            (car.address || car.consignee?.address || '').toUpperCase(),
                            (car.port?.port || car.port_code || '').toUpperCase(),
                            '',
                            relShortChassis.toUpperCase(),
                            '',
                            '',
                            '',
                            '',
                            '',
                        ]);
                        rowIsRelated.push(true);
                    });
                }
            });

            // Filter data to only selected columns
            const data = fullData.map(row => columnIndices.map(i => row[i]));

            // Create worksheet with headers and data
            const worksheetData = [headers, ...data];
            const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

            // Style definitions
            const headerStyle = {
                font: {bold: true, sz: 14},
                fill: {fgColor: {rgb: 'D4DFEF'}},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            const bodyStyle = {
                font: {sz: 11},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            const relatedStyle = {
                font: {sz: 11, italic: true},
                fill: {fgColor: {rgb: 'F0F0F0'}},
                alignment: {horizontal: 'center', vertical: 'center'},
            };

            // Calculate auto-fit column widths with improved algorithm
            const colWidths = headers.map((header, colIndex) => {
                let maxLen = header.length;
                data.forEach(row => {
                    const cellValue = String(row[colIndex] || '');
                    if (cellValue.length > maxLen) {
                        maxLen = cellValue.length;
                    }
                });
                // Better width calculation accounting for proportional fonts
                const avgCharWidth = 1.2;
                const padding = 3;
                const calculatedWidth = Math.ceil(maxLen * avgCharWidth) + padding;
                return {wch: Math.min(Math.max(calculatedWidth, 10), 60)};
            });

            // Apply header styles (row 1)
            for (let col = 0; col < headers.length; col++) {
                const cellRef = XLSX.utils.encode_cell({r: 0, c: col});
                if (worksheet[cellRef]) {
                    worksheet[cellRef].s = headerStyle;
                }
            }

            // Apply body styles (rows 2+)
            for (let row = 1; row <= data.length; row++) {
                const isRelated = rowIsRelated[row - 1];
                for (let col = 0; col < headers.length; col++) {
                    const cellRef = XLSX.utils.encode_cell({r: row, c: col});
                    if (worksheet[cellRef]) {
                        worksheet[cellRef].s = isRelated ? relatedStyle : bodyStyle;
                    }
                }
            }

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Cars');

            // Set auto-fit column widths
            worksheet['!cols'] = colWidths;

            const filename = `Cars_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
            XLSX.writeFile(workbook, filename);

            showToast?.(`Exported ${selectedCars.length} cars to Excel`, 'success');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            showToast?.('Failed to export to Excel', 'error');
        }
    };

    const handleActionClick = (action: BulkActionKey) => {
        if (action === 'print_bill_of_lading') {
            setShowPrintBookingConfirmationSubmenu(false);
            setShowPrintBLSubmenu(!showPrintBLSubmenu);
        } else if (action === 'print_booking_confirmation') {
            setShowPrintBLSubmenu(false);
            setShowPrintBookingConfirmationSubmenu(!showPrintBookingConfirmationSubmenu);
        } else if (action === 'export_excel') {
            handleExportToExcel();
        } else {
            setShowActionsMenu(false);
            onAction(action);
        }
    };

    const hasAnyBulkAction = actionGroups.some(group => group.actions.length > 0);

    const effectiveCount = displayCount ?? selectedCarIds.length;

    if (effectiveCount === 0 || !hasAnyBulkAction) {
        return null;
    }

    return (
        <div className="flex items-center gap-2" data-actions-menu>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {effectiveCount} {effectiveCount === 1 ? 'car' : 'cars'} selected
            </span>
            <div className="relative">
                <button
                    onClick={() => setShowActionsMenu(prev => !prev)}
                    className="px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors flex items-center"
                >
                    Actions
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>

                {showActionsMenu && (
                    <div
                        className="absolute right-0 mt-2 w-64 origin-top-right rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 shadow-lg ring-1 ring-black/10 backdrop-blur-md z-50">
                        <div className="py-2 max-h-96 overflow-y-auto">
                            {actionGroups.map(group => (
                                <div key={group.title} className="py-1">
                                    <div
                                        className="px-4 pb-1 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        {group.title}
                                    </div>
                                    {group.actions.length === 0 ? (
                                        <div className="px-4 py-2 text-xs text-gray-400 dark:text-gray-500">
                                            No actions available
                                        </div>
                                    ) : (
                                        group.actions.map(action => (
                                            <div key={action.key} className="relative">
                                                <button
                                                    ref={action.key === 'print_bill_of_lading' ? printBLButtonRef : action.key === 'print_booking_confirmation' ? printBookingConfirmationButtonRef : null}
                                                    onClick={() => handleActionClick(action.key)}
                                                    disabled={action.key === 'print_bill_of_lading' && generatingBL}
                                                    className={`w-full text-left px-4 py-2 text-sm transition-colors flex items-center justify-between ${
                                                        action.key === 'delete'
                                                            ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30'
                                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                                    } ${generatingBL && action.key === 'print_bill_of_lading' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <span>{action.label}</span>
                                                    {(action.key === 'print_bill_of_lading' || action.key === 'print_booking_confirmation') && (
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                             viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                  strokeWidth={2} d="M9 5l7 7-7 7"/>
                                                        </svg>
                                                    )}
                                                </button>
                                            </div>
                                        ))
                                    )}
                                    <div className="mt-2 border-t border-gray-200 dark:border-gray-700"/>
                                </div>
                            ))}
                        </div>
                        {showPrintBLSubmenu && (
                            <div
                                ref={printBLSubmenuRef}
                                className="absolute left-full ml-2 bottom-0 w-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 shadow-lg ring-1 ring-black/10 backdrop-blur-md z-[60]"
                            >
                                <div className="py-2">
                                    <div
                                        className="px-3 pb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                                        BL Type
                                    </div>
                                    <div className="px-3 py-2 space-y-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="blType"
                                                checked={isOriginalBL}
                                                onChange={() => setIsOriginalBL(true)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-xs text-gray-700 dark:text-gray-300">Original</span>
                                        </label>
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="radio"
                                                name="blType"
                                                checked={!isOriginalBL}
                                                onChange={() => setIsOriginalBL(false)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                            />
                                            <span className="text-xs text-gray-700 dark:text-gray-300">Copy</span>
                                        </label>
                                    </div>
                                    <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={includeRelated}
                                                onChange={(e) => setIncludeRelated(e.target.checked)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-xs text-gray-700 dark:text-gray-300">Include Related</span>
                                        </label>
                                    </div>
                                    {selectedCarIds.length === 1 ? (
                                        <button
                                            onClick={handlePrintSingleBL}
                                            disabled={generatingBL}
                                            className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                        >
                                            {generatingBL ? 'Generating...' : 'Print Bill of Lading'}
                                        </button>
                                    ) : (
                                        <>
                                            <div
                                                className="px-3 pb-2 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 border-t border-b border-gray-200 dark:border-gray-700 mt-2 pt-2">
                                                Print Options
                                            </div>
                                            <button
                                                onClick={handlePrintCombinedBL}
                                                disabled={generatingBL}
                                                className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                            >
                                                {generatingBL ? 'Generating...' : 'Print Combined BL'}
                                            </button>
                                            <button
                                                onClick={handlePrintBLZip}
                                                disabled={generatingBL}
                                                className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
                                            >
                                                {generatingBL ? 'Generating...' : 'Print ZIP (Multiple PDFs)'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        {showPrintBookingConfirmationSubmenu && (
                            <div
                                ref={printBookingConfirmationSubmenuRef}
                                className="absolute left-full ml-2 bottom-0 w-48 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 shadow-lg ring-1 ring-black/10 backdrop-blur-md z-[60]"
                            >
                                <div className="py-2">
                                    <div className="px-3 py-2">
                                        <label className="flex items-center gap-2 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={includeRelatedBookingConfirmation}
                                                onChange={(e) => setIncludeRelatedBookingConfirmation(e.target.checked)}
                                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                                            />
                                            <span className="text-xs text-gray-700 dark:text-gray-300">Print Related Units</span>
                                        </label>
                                    </div>
                                    <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                                        <button
                                            onClick={handlePrintBookingConfirmation}
                                            className="w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                        >
                                            Print Booking Confirmation
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <LoadingOverlay
                isVisible={generatingBL}
                message="Generating PDFs..."
                subMessage="Please wait while we prepare your ZIP file"
            />
            <Modal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                title="Select Columns to Export"
                size="sm"
                footer={
                    <div className="flex gap-3 justify-end">
                        <button
                            onClick={() => setShowExportModal(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleConfirmExport}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                        >
                            Export to Excel
                        </button>
                    </div>
                }
            >
                <div className="space-y-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Select the columns you want to include in the Excel export:
                    </p>
                    {EXPORT_COLUMNS.filter(column => column.key !== 'address').map(column => (
                        <label
                            key={column.key}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                        >
                            <input
                                type="checkbox"
                                checked={selectedColumns[column.key]}
                                onChange={(e) => setSelectedColumns(prev => ({
                                    ...prev,
                                    [column.key]: e.target.checked
                                }))}
                                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {column.label}
                            </span>
                        </label>
                    ))}
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={includeDimensions}
                                onChange={(e) => setIncludeDimensions(e.target.checked)}
                                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Dimensions
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                (Length, Width, Height, CBM, Weight)
                            </span>
                        </label>
                        <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors">
                            <input
                                type="checkbox"
                                checked={selectedColumns['address'] ?? false}
                                onChange={(e) => setSelectedColumns(prev => ({
                                    ...prev,
                                    address: e.target.checked
                                }))}
                                className="w-4 h-4 text-blue-600 bg-gray-100 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded focus:ring-blue-500 focus:ring-2"
                            />
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Address
                            </span>
                        </label>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

